'use client';

import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { VipIcon } from '@/app/components/icons/VipIcon';
import { PlanDialogScene } from '@/app/components/shared/PlanDetailDialog';
import { TaskSubmitButton } from '@/app/components/shared/TaskSubmitButton';
import { SITE } from '@/app/constants';
import { useAdsDialog, useFetch, useGlobalModal, useRunTempUploadBatch, useTrials } from '@/app/hooks';
import { useStore, useStoreApi } from '@/app/providers/store';
import type { FormAudioPrompt, SourceAudio } from '@/app/types';
import { reportEvent } from '@/app/utils';
import type { UploadResult } from '@/app/utils/client';
import { usePathname } from '@/i18n/routing';
import { useCreationConsent } from '../_hooks/use-creation-consent';
import { createPromptListHelpers } from '../_utils';
import { getToolTempAudioPendingItems } from '../_utils/tool-temp-upload-items';
import { AudioPlayer } from './_components/AudioPlayer';
import { AudioRecorder } from './_components/AudioRecorder';
import { ReadingTextSelector } from './_components/ReadingTextSelector';
import { VoiceCloneJobCard } from './_components/VoiceCloneJobCard';
import { useVoiceCloneSubmit } from './_hooks/use-voice-clone-submit';
import { fetchVoiceCloneJobsFromApi, mergeServerVoiceCloneJobs } from './_utils/voice-clone-jobs-from-api';
import { getVoiceCloneCost } from './apis';
import { VOICE_CLONE_MAX_PARALLEL_JOBS } from './constants';
import type { AudioRecordingFile, MaterialV1Voice, VoiceCloneJobItem } from './types';
import { formatRecordingName, getNextReadingText, getVoiceCloneReadingTextLang, pickRandomReadingText } from './utils';

const voiceCloneReadingTextLang = getVoiceCloneReadingTextLang(SITE);

export default function VoiceCloneForm() {
  const t = useTranslations();
  const pathname = usePathname();
  const storeApi = useStoreApi();
  const { open: openConfirmDialog } = useGlobalModal('confirmDialog');
  const { open: openLoginDialog } = useGlobalModal('loginDialog');
  const { open: openPlanDetailDialog } = useGlobalModal('planDetailDialog');
  const { open: openPurchaseCreditsDialog } = useGlobalModal('purchaseCreditsDialog');
  const { tryOpenInsufficientCreditsDialog } = useAdsDialog();
  const [readingText, setReadingText] = useState(() => pickRandomReadingText(voiceCloneReadingTextLang));
  const [voiceCloneJobs, setVoiceCloneJobs] = useState<VoiceCloneJobItem[]>([]);

  const { voiceCloneFormParams, updateVoiceCloneFormParams, userInfo, creditsInfo, isTeamSpace, trialsVoice } =
    useStore((state) => ({
      voiceCloneFormParams: state.voiceCloneFormParams,
      updateVoiceCloneFormParams: state.updateVoiceCloneFormParams,
      userInfo: state.userInfo,
      creditsInfo: state.creditsInfo,
      isTeamSpace: state.isTeamSpace,
      trialsVoice: state.trialsVoice,
    }));

  const { updateTrialsAsync } = useTrials();

  const handleVoiceCreateAccepted = useCallback(() => {
    void updateTrialsAsync();
  }, [updateTrialsAsync]);

  const handleVoiceProcessingStart = useCallback(
    ({ voiceId }: { voiceId: string }) => {
      const audio = storeApi.getState().voiceCloneFormParams.audioPrompt?.prompt;
      if (!audio) return;
      setVoiceCloneJobs((prev) => {
        const item: VoiceCloneJobItem = {
          voiceId,
          cardStatus: 'queued',
          audio: { ...audio },
          voiceName: audio.name ?? '',
          voiceDescription: '',
          jobSource: 'form',
        };
        return [item, ...prev.filter((j) => j.voiceId !== voiceId)];
      });
      // 受理成功：本段已提交，清空表单录音，避免重复提交并便于录下一段
      updateVoiceCloneFormParams('audioPrompt', undefined);
    },
    [storeApi, updateVoiceCloneFormParams],
  );

  const handleVoiceSucceeded = useCallback((voice: MaterialV1Voice) => {
    const voiceId = voice.voice_id;
    if (!voiceId) return;
    setVoiceCloneJobs((prev) =>
      prev.map((j) => {
        if (j.voiceId !== voiceId) return j;
        const sampleUri = voice.sample_uri?.trim();
        const { preview: _discardPreview, ...resultAudio } = j.audio;
        return {
          ...j,
          cardStatus: 'succeeded' as const,
          audio: { ...resultAudio, src: sampleUri ?? '' },
          voiceName: voice.name ?? j.voiceName,
          voiceDescription: voice.description ?? j.voiceDescription,
          listTaskStatus: 'success',
        };
      }),
    );
  }, []);

  const handleVoiceProcessing = useCallback(({ voiceId }: { voiceId: string }) => {
    setVoiceCloneJobs((prev) =>
      prev.map((j) =>
        j.voiceId === voiceId ? { ...j, cardStatus: 'processing' as const, listTaskStatus: 'processing' } : j,
      ),
    );
  }, []);

  const handleVoiceFailed = useCallback(({ voiceId }: { voiceId: string }) => {
    setVoiceCloneJobs((prev) =>
      prev.map((j) => (j.voiceId === voiceId ? { ...j, cardStatus: 'failed' as const, listTaskStatus: 'failed' } : j)),
    );
  }, []);

  const { data: voiceCloneCostRes } = useFetch(() => getVoiceCloneCost(), {
    refreshDeps: [userInfo?.id],
    suppressUnauthorizedLoginDialog: true,
  });

  const [costCredits, originalCredits] = useMemo(() => {
    const cost = voiceCloneCostRes?.data?.cost_credits;
    const original = voiceCloneCostRes?.data?.original_cost_credits;
    if (original !== undefined && cost !== undefined && original > cost) {
      return [cost, original] as const;
    }
    return [cost, undefined] as const;
  }, [voiceCloneCostRes]);

  const hasVoiceTrialRemaining = (trialsVoice?.remaining ?? 0) > 0;

  const displayCostCredits = useMemo(() => {
    if (hasVoiceTrialRemaining) {
      return 0 as const;
    }
    return costCredits;
  }, [costCredits, hasVoiceTrialRemaining]);

  const displayOriginalCredits = useMemo(() => {
    if (hasVoiceTrialRemaining) {
      return undefined;
    }
    return originalCredits;
  }, [originalCredits, hasVoiceTrialRemaining]);

  const creditsInsufficient = useMemo(
    () =>
      !!(
        displayCostCredits !== undefined &&
        displayCostCredits > 0 &&
        creditsInfo &&
        creditsInfo.total < displayCostCredits
      ),
    [displayCostCredits, creditsInfo],
  );

  const ensureVoiceConsent = useCreationConsent(userInfo?.id, { requireVoiceConsent: SITE === 'china' });
  const audioPrompt = voiceCloneFormParams.audioPrompt;
  const sourceAudio = audioPrompt?.prompt ?? null;
  const audioPreview = sourceAudio?.preview;
  const isAudioUploading = audioPrompt?.status === 'uploading' || !!(userInfo && sourceAudio && !sourceAudio.src);

  const {
    phase,
    submit: submitVoiceClone,
    reset: resetVoiceClone,
  } = useVoiceCloneSubmit({
    onCreateAccepted: handleVoiceCreateAccepted,
    onVoiceProcessingStart: handleVoiceProcessingStart,
  });

  const handleAudioUploadError = useCallback(
    (error: UploadResult['error']) => {
      updateVoiceCloneFormParams('audioPrompt', undefined);
      resetVoiceClone();

      if (error?.reason === 'AuditSubmitIllegal' || error?.reason === 'SensitiveContentDetected') {
        const auditId = error.metadata?.audit_id;
        openConfirmDialog({
          title: t('popup.dialog.error.checkFailed.title'),
          content: auditId
            ? `${t('sharedComponent.audioUploader.error.checkFailedContent')}\n(ID: ${auditId})`
            : t('sharedComponent.audioUploader.error.checkFailedContent'),
          primaryButtonText: t('popup.dialog.error.confirm'),
        });
        return;
      }

      openConfirmDialog({
        title: t('popup.toast.uploadingError'),
        primaryButtonText: t('popup.dialog.error.confirm'),
      });
    },
    [openConfirmDialog, resetVoiceClone, t, updateVoiceCloneFormParams],
  );

  useEffect(() => {
    if (!userInfo?.id) return;
    let cancelled = false;
    void (async () => {
      try {
        const serverJobs = await fetchVoiceCloneJobsFromApi();
        if (cancelled) return;
        setVoiceCloneJobs((prev) => mergeServerVoiceCloneJobs(prev, serverJobs));
      } catch {
        // 未登录或网络异常时跳过列表预取
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userInfo?.id]);

  useRunTempUploadBatch(
    userInfo,
    'Failed to upload voice clone audio',
    (refs) =>
      getToolTempAudioPendingItems(
        refs,
        createPromptListHelpers(
          () => {
            const prompt = storeApi.getState().voiceCloneFormParams.audioPrompt;
            return prompt ? [prompt] : [];
          },
          (prompts) => {
            updateVoiceCloneFormParams('audioPrompt', prompts.filter(Boolean)[0] as FormAudioPrompt | undefined);
          },
        ),
        { audioPrompts: audioPrompt ? [audioPrompt] : [], onUploadError: handleAudioUploadError },
      ),
    [audioPrompt, handleAudioUploadError, updateVoiceCloneFormParams, userInfo],
  );

  useEffect(() => {
    return () => {
      if (audioPreview) {
        URL.revokeObjectURL(audioPreview);
      }
    };
  }, [audioPreview]);

  const handleRecordingComplete = useCallback(
    (file: AudioRecordingFile) => {
      const id = uuidv4();
      const fileExtension = file.blob.type === 'audio/mp4' ? 'm4a' : 'webm';
      const audioFile = new File([file.blob], `voice-clone-${id}.${fileExtension}`, { type: file.blob.type });
      const newSourceAudio: SourceAudio = {
        id: `temp-audio-${id}`,
        tempId: id,
        name: formatRecordingName(new Date()),
        src: '',
        preview: file.url,
        duration: file.duration,
        _tempFile: audioFile,
      };

      resetVoiceClone();
      updateVoiceCloneFormParams('audioPrompt', {
        id,
        status: userInfo ? 'uploading' : 'done',
        prompt: newSourceAudio,
      });
    },
    [userInfo, updateVoiceCloneFormParams, resetVoiceClone],
  );

  const deleteRecording = useCallback(() => {
    updateVoiceCloneFormParams('audioPrompt', undefined);
    resetVoiceClone();
  }, [updateVoiceCloneFormParams, resetVoiceClone]);

  const handleDeleteRecording = useCallback(() => {
    openConfirmDialog({
      title: t('tools.voiceClone.player.deleteConfirmTitle'),
      content: t('tools.voiceClone.player.deleteConfirmContent'),
      primaryButtonText: t('tools.voiceClone.player.deleteConfirmPrimary'),
      secondaryButtonText: t('tools.voiceClone.player.deleteConfirmSecondary'),
      onPrimaryAction: deleteRecording,
    });
  }, [deleteRecording, openConfirmDialog, t]);

  const handleChangeReadingText = useCallback(() => {
    setReadingText((currentText) => getNextReadingText(currentText, voiceCloneReadingTextLang));
  }, []);

  const handleVoiceNameChange = useCallback((voiceId: string, voiceName: string) => {
    setVoiceCloneJobs((jobs) =>
      jobs.map((j) => (j.voiceId === voiceId ? { ...j, audio: { ...j.audio, name: voiceName }, voiceName } : j)),
    );
  }, []);

  const handleVoiceDescriptionChange = useCallback((voiceId: string, voiceDescription: string) => {
    setVoiceCloneJobs((jobs) => jobs.map((j) => (j.voiceId === voiceId ? { ...j, voiceDescription } : j)));
  }, []);

  const disabledTip = useMemo(() => {
    if (!sourceAudio) {
      return t('tools.voiceClone.validation.audioRequired');
    }
    if (isAudioUploading) {
      return t('create.createForm.error.uploadingMedia');
    }
    return '';
  }, [isAudioUploading, sourceAudio, t]);

  const hasReachedParallelJobLimit = voiceCloneJobs.length >= VOICE_CLONE_MAX_PARALLEL_JOBS;

  const submitDisabledTip = useMemo(() => {
    if (disabledTip) return disabledTip;
    if (hasReachedParallelJobLimit) return t('tools.voiceClone.submit.waitWhileProcessing');
    return '';
  }, [disabledTip, hasReachedParallelJobLimit, t]);

  const handleCreditsInsufficientClick = useCallback(() => {
    if (isTeamSpace) {
      openConfirmDialog({
        title: t('popup.dialog.error.teamCreditsInsufficientContactAdmin.title'),
        description: t('popup.dialog.error.teamCreditsInsufficientContactAdmin.content'),
        primaryButtonText: t('popup.dialog.error.confirm'),
      });
      return;
    }

    if (userInfo?.subs_plan === 'free') {
      openPlanDetailDialog({ scene: PlanDialogScene.InsufficientCredits });
      return;
    }

    openPurchaseCreditsDialog({
      showTriggerButton: false,
      onClose: () => {
        tryOpenInsufficientCreditsDialog();
      },
    });
    reportEvent('purchase_credits_exposure', { from_position: 'voice_clone -null' });
  }, [
    isTeamSpace,
    openConfirmDialog,
    openPlanDetailDialog,
    openPurchaseCreditsDialog,
    t,
    tryOpenInsufficientCreditsDialog,
    userInfo?.subs_plan,
  ]);

  const handleSubmit = useCallback(() => {
    if (!userInfo) {
      openLoginDialog();
      reportEvent('login_pop_exposure', {
        refer_element: 'create - submit',
        refer_page: pathname,
      });
      return;
    }
    if (userInfo.subs_plan === 'free' && !hasVoiceTrialRemaining) {
      openPlanDetailDialog();
      return;
    }
    if (!audioPrompt?.prompt?.src) {
      return;
    }
    submitVoiceClone(audioPrompt.prompt.src, sourceAudio?.name ?? '', hasVoiceTrialRemaining);
  }, [
    userInfo,
    openLoginDialog,
    openPlanDetailDialog,
    pathname,
    audioPrompt,
    sourceAudio,
    submitVoiceClone,
    hasVoiceTrialRemaining,
  ]);

  return (
    <div className="flex w-full flex-none flex-col bg-[#070c0e] px-10 pt-8 pb-0 text-sm sm:px-4 sm:pt-4">
      <div className="mx-auto flex w-full max-w-[936px] flex-col gap-5">
        <div className="space-y-2">
          <h1 className="font-semibold text-[32px] text-system-white leading-[1.4]">{t('tools.voiceClone.title')}</h1>
          <p className="text-sm text-system-white/40 leading-[1.4]">{t('tools.voiceClone.description')}</p>
        </div>

        <div className="space-y-4">
          <div className="relative flex flex-col items-center justify-center rounded-16 bg-[#202126] p-8 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.05)] sm:min-h-[220px] sm:px-4">
            <button
              type="button"
              onClick={handleChangeReadingText}
              className="absolute top-4 right-4 rounded-full bg-system-white08 px-3 py-1.5 font-semibold text-system-white/72 text-xs transition hover:bg-system-white12 hover:text-system-white"
            >
              {t('tools.voiceClone.readingText.change')}
            </button>
            <ReadingTextSelector value={readingText} readingTextLang={voiceCloneReadingTextLang} />
            <div className="mt-5">
              <AudioRecorder
                onRecordingComplete={handleRecordingComplete}
                onMicrophonePermissionGranted={ensureVoiceConsent}
                blockNewRecording={!!sourceAudio}
                consentHint={t('tools.voiceClone.recorder.consentHint')}
              />
            </div>
          </div>
          {sourceAudio && (
            <>
              <div className="text-system-white/40 text-xs leading-[1.4]">
                {t('tools.voiceClone.player.trialLabel')}
              </div>
              <AudioPlayer audio={sourceAudio} onDelete={handleDeleteRecording} />
            </>
          )}
        </div>

        {sourceAudio && (
          <div className="flex w-full justify-center">
            <TaskSubmitButton
              className="w-[340px]"
              disabledTip={submitDisabledTip}
              submitting={phase === 'submitting' || isAudioUploading}
              costCredits={displayCostCredits}
              originalCredits={displayOriginalCredits}
              creditsInsufficient={creditsInsufficient}
              creditsVariant="currentColor"
              extra={
                hasVoiceTrialRemaining && !submitDisabledTip ? (
                  <div className="absolute top-0 right-0 rounded-bl-12 bg-system-blue02/80 px-[14px] py-[2px] font-medium text-sm text-white">
                    {t('tools.voiceClone.submit.freeTrialLeft', { time: trialsVoice?.remaining ?? 0 })}
                  </div>
                ) : undefined
              }
              onClick={creditsInsufficient ? handleCreditsInsufficientClick : handleSubmit}
            >
              <span className="flex items-center">
                {!submitDisabledTip && <VipIcon className="mr-2 inline-block shrink-0 rounded-full bg-[#7157f5]/70" />}
                {t('create.createForm.submit')}
              </span>
            </TaskSubmitButton>
          </div>
        )}

        {voiceCloneJobs.length > 0 && (
          <>
            <div className="text-system-white/40 text-xs leading-[1.4]">
              {t('tools.voiceClone.voiceInfo.previewLabel')}
            </div>
            <ul className="flex list-none flex-col gap-4 p-0">
              {voiceCloneJobs.map((job) => (
                <li key={job.voiceId}>
                  <VoiceCloneJobCard
                    job={job}
                    onVoiceNameChange={handleVoiceNameChange}
                    onVoiceDescriptionChange={handleVoiceDescriptionChange}
                    onVoiceSucceeded={handleVoiceSucceeded}
                    onVoiceProcessing={handleVoiceProcessing}
                    onVoiceFailed={handleVoiceFailed}
                  />
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </div>
  );
}
